/**
 * StudentLiveSatisfactionScreen — la note du live, en écran (`live-satisfaction`, `rating` et
 * `liveSessionId` en paramètres) : ce qui suit les étoiles de l'ancienne boîte
 * (`LiveSessionDialogComponent`), les chips de la note (`send-student-comment`), puis « Terminer ».
 *
 * L'écran du live du design system ne pousse plus cette route : sa note et ses chips partent
 * ensemble, dans la feuille (`useLiveLeave`). Elle reste servie pour qui la pousse encore : la même
 * feuille (`<LiveRatingModal>`), ouverte sur la note reçue, puis le retour.
 */
import { LiveRatingModal } from "../../../../../../design-system/student"
import { useNavigation } from "@react-navigation/native"
import React, { useMemo, useState } from "react"
import { useDispatch, useSelector } from "react-redux"

import { liveService } from "../../../../../../data/services/liveService"
import { apiCall } from "../../../../../../engine/shared/apiCall"
import { createTelemetry } from "../../../../../../engine/shared/telemetry"
import { useStudentThemeScope } from "../../../hooks/useStudentThemeScope"
import { type StudentSpaceScreenProps } from "../studentSpaceRouteTypes"
import { selectDisplayedStudent } from "../../../../view-models/shared/displayedStudent"

type RootState = {
    data?: {
        staticData?: {
            satisfaction_student_comments?: Record<string, string[]> | null
            satisfaction_subtitles?: Record<string, string> | null
        }
        currentUser?: {
            object?: { live_sessions?: { id?: number; tutor?: { display_name?: string | null } }[] } | null
        }
    }
}

type Navigation = { canGoBack: () => boolean; goBack: () => void; replace: (route: string) => void }

export function StudentLiveSatisfactionScreen({ route }: StudentSpaceScreenProps) {
    useStudentThemeScope()
    const params = (route?.params ?? {}) as { rating?: number | string; liveSessionId?: number | string }
    const liveSessionId = params.liveSessionId != null ? Number(params.liveSessionId) : null
    const navigation = useNavigation() as unknown as Navigation
    const dispatch = useDispatch()
    const telemetry = useMemo(() => createTelemetry("live-student.satisfaction", { role: "student" }), [])
    const comments = useSelector((state: RootState) => state.data?.staticData?.satisfaction_student_comments)
    const subtitles = useSelector((state: RootState) => state.data?.staticData?.satisfaction_subtitles)
    const tutorName = useSelector(
        (state: RootState) =>
            selectDisplayedStudent(state)?.live_sessions?.find(
                (session) => String(session?.id) === String(liveSessionId)
            )?.tutor?.display_name
    )
    const [rating, setRating] = useState<number | null>(params.rating != null ? Number(params.rating) : null)
    const [selected, setSelected] = useState<string[]>([])
    const [submitting, setSubmitting] = useState(false)

    const close = () => (navigation.canGoBack() ? navigation.goBack() : navigation.replace("menu"))
    const submit = () => {
        if (liveSessionId == null || rating == null) return close()
        setSubmitting(true)
        apiCall(dispatch as never, liveService.sendSatisfaction, {
            routeParams: { id: liveSessionId },
            data: { satisfaction: rating },
        })
            .then(() =>
                selected.length > 0
                    ? apiCall(dispatch as never, liveService.sendStudentComment, {
                          routeParams: { id: liveSessionId },
                          data: { student_comment: selected },
                      })
                    : undefined
            )
            .catch((cause: unknown) => {
                telemetry.error("send_rating", cause)
            })
            .finally(() => {
                setSubmitting(false)
                close()
            })
    }

    return (
        <LiveRatingModal
            visible
            tutorFirstname={(tutorName ?? "").trim().split(/\s+/)[0] || "ton masteur"}
            rating={rating}
            onRate={(value) => {
                setRating(value)
                setSelected([])
            }}
            subtitle={rating != null ? (subtitles?.[String(rating)] ?? undefined) : undefined}
            comments={rating != null ? (comments?.[String(rating)] ?? []) : []}
            selectedComments={selected}
            onToggleComment={(comment) =>
                setSelected((list) =>
                    list.includes(comment) ? list.filter((item) => item !== comment) : [...list, comment]
                )
            }
            onDismiss={close}
            onSubmit={submit}
            submitting={submitting}
        />
    )
}
